import { useState, useCallback } from 'react'
import type { Book, BookFilters } from '@/types'

/**
 * Filter visibility configuration
 */
export interface FilterVisibilityConfig {
    language: boolean
    category: boolean
    author: boolean
    series: boolean
    publisher: boolean
    format: boolean
    status: boolean
    progress: boolean
    location: boolean
    biblionauta: boolean
    [key: string]: boolean
}

/**
 * Parameters for useBookFilters hook
 */
interface UseBookFiltersParams {
    isBiblioverso: boolean
    books: Book[]
}

/**
 * Return type for useBookFilters hook
 */
interface UseBookFiltersReturn {
    // Filter state
    filters: BookFilters
    visibleFilters: FilterVisibilityConfig
    showFilters: boolean
    showFilterSettings: boolean
    searchTerm: string

    // Setters
    setShowFilters: (show: boolean) => void
    setShowFilterSettings: (show: boolean) => void
    setSearchTerm: (term: string) => void
    setVisibleFilters: (filters: FilterVisibilityConfig) => void

    // Filter operations
    setFilter: (filterName: keyof BookFilters, values: string[]) => void
    clearFilter: (filterName: keyof BookFilters) => void
    clearAllFilters: () => void
    toggleFilterVisibility: (filterName: keyof FilterVisibilityConfig) => void
    getFilteredBooks: (searchTermOverride?: string) => Book[]
    getFilteredBooksExcluding: (excludeFilter: keyof BookFilters) => Book[]
}

/**
 * Custom hook for managing book filters
 * 
 * Handles filter state, visibility, and filtering logic for book collections
 */
export function useBookFilters({ isBiblioverso, books }: UseBookFiltersParams): UseBookFiltersReturn {
    // Filter states
    const [filters, setFilters] = useState<BookFilters>({
        author: [],
        format: [],
        status: [],
        progress: [],
        publisher: [],
        location: [],
        series: [],
        category: [],
        language: [],
        biblionauta: [],
    })

    // Filter visibility settings
    const [visibleFilters, setVisibleFilters] = useState<FilterVisibilityConfig>({
        language: true,
        category: true,
        author: true,
        series: true,
        publisher: true,
        format: true,
        status: true,
        progress: !isBiblioverso, // Default hidden in Biblioverso
        location: !isBiblioverso, // Default hidden in Biblioverso
        biblionauta: isBiblioverso, // Only visible in Biblioverso
    })

    const [showFilters, setShowFilters] = useState(false)
    const [showFilterSettings, setShowFilterSettings] = useState(false)
    const [searchTerm, setSearchTerm] = useState('')

    // Set a specific filter
    const setFilter = useCallback((filterName: keyof BookFilters, values: string[]) => {
        setFilters((prev) => ({ ...prev, [filterName]: values }))
    }, [])

    // Clear a specific filter
    const clearFilter = useCallback((filterName: keyof BookFilters) => {
        setFilters((prev) => ({ ...prev, [filterName]: [] }))
    }, [])

    // Clear all filters
    const clearAllFilters = useCallback(() => {
        setFilters({
            author: [],
            format: [],
            status: [],
            progress: [],
            publisher: [],
            location: [],
            series: [],
            category: [],
            language: [],
            biblionauta: [],
        })
        setSearchTerm('')
    }, [])

    // Toggle filter visibility
    const toggleFilterVisibility = useCallback((filterName: keyof FilterVisibilityConfig) => {
        setVisibleFilters((prev) => ({ ...prev, [filterName]: !prev[filterName] }))
    }, [])

    // Apply filters to books array
    const getFilteredBooks = useCallback(
        (searchTermOverride: string = searchTerm): Book[] => {
            return books.filter((book) => {
                // Search in title or ANY author
                const matchesSearch =
                    searchTermOverride === '' ||
                    book.title?.toLowerCase().includes(searchTermOverride.toLowerCase()) ||
                    (Array.isArray(book.author) &&
                        book.author.some((a) => a?.toLowerCase().includes(searchTermOverride.toLowerCase())))

                // Match ANY author in the array
                const matchesAuthor =
                    filters.author.length === 0 ||
                    (Array.isArray(book.author) && book.author.some((a) => filters.author.includes(a)))

                const matchesFormat = filters.format.length === 0 || filters.format.includes(book.format || '')
                const matchesStatus = filters.status.length === 0 || filters.status.includes(book.status || '')
                const matchesProgress = filters.progress.length === 0 || filters.progress.includes(book.progress || '')
                const matchesPublisher =
                    filters.publisher.length === 0 || filters.publisher.includes(book.publisher || '')
                const matchesLocation =
                    filters.location.length === 0 || filters.location.includes(book.location || '')
                const matchesSeries = filters.series.length === 0 || filters.series.includes(book.series || '')

                // Match ANY category in the array
                const matchesCategory =
                    filters.category.length === 0 ||
                    (Array.isArray(book.category) &&
                        book.category.some((c) => filters.category.includes(c)))

                const matchesLanguage = filters.language.length === 0 || filters.language.includes(book.language || '')

                // Biblionauta filter (owner filter in Biblioverso)
                const matchesBiblionauta =
                    filters.biblionauta.length === 0 || filters.biblionauta.includes(book.user_id || '')

                return (
                    matchesSearch &&
                    matchesAuthor &&
                    matchesFormat &&
                    matchesStatus &&
                    matchesProgress &&
                    matchesPublisher &&
                    matchesLocation &&
                    matchesSeries &&
                    matchesCategory &&
                    matchesLanguage &&
                    matchesBiblionauta
                )
            })
        },
        [books, filters, searchTerm]
    )

    // Apply filters to books array, excluding a specific filter
    const getFilteredBooksExcluding = useCallback(
        (excludeFilter: keyof BookFilters): Book[] => {
            return books.filter((book) => {
                // Search in title or ANY author
                const matchesSearch =
                    searchTerm === '' ||
                    book.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                    (Array.isArray(book.author) &&
                        book.author.some((a) => a?.toLowerCase().includes(searchTerm.toLowerCase())))

                // Match ANY author in the array
                const matchesAuthor =
                    excludeFilter === 'author' ||
                    filters.author.length === 0 ||
                    (Array.isArray(book.author) && book.author.some((a) => filters.author.includes(a)))

                const matchesFormat =
                    excludeFilter === 'format' ||
                    filters.format.length === 0 ||
                    filters.format.includes(book.format || '')
                const matchesStatus =
                    excludeFilter === 'status' ||
                    filters.status.length === 0 ||
                    filters.status.includes(book.status || '')
                const matchesProgress =
                    excludeFilter === 'progress' ||
                    filters.progress.length === 0 ||
                    filters.progress.includes(book.progress || '')
                const matchesPublisher =
                    excludeFilter === 'publisher' ||
                    filters.publisher.length === 0 ||
                    filters.publisher.includes(book.publisher || '')
                const matchesLocation =
                    excludeFilter === 'location' ||
                    filters.location.length === 0 ||
                    filters.location.includes(book.location || '')
                const matchesSeries =
                    excludeFilter === 'series' ||
                    filters.series.length === 0 ||
                    filters.series.includes(book.series || '')

                // Match ANY category in the array
                const matchesCategory =
                    excludeFilter === 'category' ||
                    filters.category.length === 0 ||
                    (Array.isArray(book.category) &&
                        book.category.some((c) => filters.category.includes(c)))

                const matchesLanguage =
                    excludeFilter === 'language' ||
                    filters.language.length === 0 ||
                    filters.language.includes(book.language || '')

                // Biblionauta filter (owner filter in Biblioverso)
                const matchesBiblionauta =
                    excludeFilter === 'biblionauta' ||
                    filters.biblionauta.length === 0 ||
                    filters.biblionauta.includes(book.user_id || '')

                return (
                    matchesSearch &&
                    matchesAuthor &&
                    matchesFormat &&
                    matchesStatus &&
                    matchesProgress &&
                    matchesPublisher &&
                    matchesLocation &&
                    matchesSeries &&
                    matchesCategory &&
                    matchesLanguage &&
                    matchesBiblionauta
                )
            })
        },
        [books, filters, searchTerm]
    )

    return {
        // State
        filters,
        visibleFilters,
        showFilters,
        showFilterSettings,
        searchTerm,

        // Setters
        setShowFilters,
        setShowFilterSettings,
        setSearchTerm,
        setVisibleFilters,

        // Operations
        setFilter,
        clearFilter,
        clearAllFilters,
        toggleFilterVisibility,
        getFilteredBooks,
        getFilteredBooksExcluding,
    }
}
